import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { PageContainer } from '@/components/ui/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Check, Loader2, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import PersonalInfoStep from '@/components/resume-steps/PersonalInfoStep';
import MilitaryStep from '@/components/resume-steps/MilitaryStep';
import CivilianStep from '@/components/resume-steps/CivilianStep';
import EducationCredentialsStep from '@/components/resume-steps/EducationCredentialsStep';
import ReviewStep from '@/components/resume-steps/ReviewStep';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, name: 'Personal Info', component: PersonalInfoStep },
  { id: 2, name: 'Military Career', component: MilitaryStep },
  { id: 3, name: 'Additional Experience', component: CivilianStep },
  { id: 4, name: 'Education & Credentials', component: EducationCredentialsStep },
  { id: 5, name: 'Review', component: ReviewStep }
];

// Steps that save form data on navigation. Review (5) is the final step — preview/style/export
// only, with no form data to persist on navigation.
const FORM_STEPS = [1, 2, 3, 4];

// Swipe configuration
const SWIPE_THRESHOLD = 80; // Minimum horizontal distance for swipe
const SWIPE_VERTICAL_TOLERANCE = 100; // Max vertical movement allowed

function ResumeBuilder() {
  const [currentStep, setCurrentStep] = useState(1);
  const [user, setUser] = useState(null);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [pendingResumeRegen, setPendingResumeRegen] = useState(false);
  // Resume-completion (Finish Resume) surface: opens on the final Review step.
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishSaving, setFinishSaving] = useState(false);
  const navigate = useNavigate();

  // Callback for military step to signal resume may be outdated
  const handleMilitaryChanged = useCallback(() => {
    setPendingResumeRegen(true);
  }, []);
  
  // Ref to store pending form data from current step
  const pendingDataRef = useRef(null);
  // Ref to track if current step has unsaved changes
  const hasUnsavedChangesRef = useRef(false);
  // Ref to store a step-specific forward-navigation validator (returns boolean)
  const stepValidatorRef = useRef(null);
  
  // Swipe navigation refs
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchEndRef = useRef({ x: 0, y: 0 });
  const isSwiping = useRef(false);
  const contentRef = useRef(null);
  
  // Use refs for values needed in touch handlers to avoid stale closures
  const currentStepRef = useRef(currentStep);
  const navigatingRef = useRef(navigating);
  const savingRef = useRef(saving);
  
  // Keep refs in sync with state
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);
  
  useEffect(() => {
    navigatingRef.current = navigating;
  }, [navigating]);
  
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(userRes.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
      
      try {
        const resumeRes = await axios.get(`${BACKEND_URL}/api/resume`, { withCredentials: true });
        console.log('[ResumeBuilder] Loaded resume data');
        setResume(resumeRes.data);
        
        // Honor an explicit ?step=N deep-link (e.g. "Fix location" → Military Career step 2).
        // An explicit target always wins over the is_complete→Review default so a location-fix
        // link never lands on the Review step / resume regeneration.
        const _stepParam = parseInt(new URLSearchParams(window.location.search).get('step'), 10);
        if (_stepParam >= 1 && _stepParam <= STEPS.length) {
          setCurrentStep(_stepParam);
        } else if (resumeRes.data?.is_complete) {
          setCurrentStep(5);
        }
      } catch (error) {
        console.error('Failed to fetch resume:', error);
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          toast.error('Failed to load resume. Please try again.');
        }
      }
      
      setLoading(false);
    };

    fetchData();
  }, []);

  // Save resume to backend
  const saveResume = useCallback(async (updateData) => {
    console.log('[ResumeBuilder] saveResume called with:', Object.keys(updateData));
    setSaving(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/resume`,
        updateData,
        { withCredentials: true }
      );
      
      console.log('[ResumeBuilder] Save successful');
      setResume(response.data);
      hasUnsavedChangesRef.current = false;
      pendingDataRef.current = null;
      toast.success('Saved');
      return response.data;
    } catch (error) {
      console.error('[ResumeBuilder] Save failed:', error);
      toast.error('Failed to save. Please try again.');
      throw error;
    } finally {
      setSaving(false);
    }
  }, []);

  // Called by step components to register pending changes. Merges disjoint slices so a
  // consolidated step (e.g. Education + Credentials) can register from multiple children.
  const registerPendingData = useCallback((data) => {
    if (typeof data === 'function') {
      pendingDataRef.current = data;
    } else {
      const prev = (pendingDataRef.current && typeof pendingDataRef.current === 'object') ? pendingDataRef.current : {};
      pendingDataRef.current = { ...prev, ...data };
    }
    console.log('[ResumeBuilder] Registering pending data:', Object.keys(typeof pendingDataRef.current === 'object' ? pendingDataRef.current : {}));
    hasUnsavedChangesRef.current = true;
  }, []);

  // Called by step components to register a forward-navigation validator.
  // Returning false blocks forward navigation out of the current step.
  const registerStepValidator = useCallback((fn) => {
    stepValidatorRef.current = fn;
  }, []);

  // Save pending data before navigation
  const savePendingData = useCallback(async () => {
    if (pendingDataRef.current && hasUnsavedChangesRef.current) {
      console.log('[ResumeBuilder] Saving pending data before navigation');
      try {
        const data = typeof pendingDataRef.current === 'function' ? pendingDataRef.current() : pendingDataRef.current;
        await saveResume(data);
        return true;
      } catch (error) {
        console.error('[ResumeBuilder] Failed to save pending data:', error);
        return false;
      }
    }
    return true;
  }, [saveResume]);

  // Navigate to a step (with save)
  const navigateToStep = useCallback(async (targetStep) => {
    if (targetStep === currentStep) return;
    if (navigating || saving) return;

    // Step-specific forward-navigation gate (only blocks moving forward, never Back)
    if (targetStep > currentStep && stepValidatorRef.current) {
      const canProceed = stepValidatorRef.current();
      if (!canProceed) return;
    }
    
    setNavigating(true);
    console.log(`[ResumeBuilder] Navigating from step ${currentStep} to step ${targetStep}`);
    
    // Only save if we're leaving a form step
    if (FORM_STEPS.includes(currentStep)) {
      const saveSuccess = await savePendingData();
      if (!saveSuccess) {
        setNavigating(false);
        toast.error('Please fix save errors before navigating');
        return;
      }
    }
    
    setCurrentStep(targetStep);
    window.scrollTo(0, 0);
    
    // Reset pending data for new step
    pendingDataRef.current = null;
    hasUnsavedChangesRef.current = false;
    // Clear the previous step's forward-navigation validator so it can never fire when
    // ENTERING another step. The destination step re-registers its own validator on mount.
    // (Fixes stale MilitaryStep validator blocking re-entry into Military Career.)
    stepValidatorRef.current = null;
    setNavigating(false);
  }, [currentStep, navigating, saving, savePendingData]);

  const handleNext = useCallback(async () => {
    if (currentStep < STEPS.length) {
      await navigateToStep(currentStep + 1);
    }
  }, [currentStep, navigateToStep]);

  const handlePrevious = useCallback(async () => {
    if (currentStep > 1) {
      await navigateToStep(currentStep - 1);
    }
  }, [currentStep, navigateToStep]);

  const handleStepClick = useCallback(async (stepId) => {
    await navigateToStep(stepId);
  }, [navigateToStep]);

  // Finish Resume (final Review step): persist any pending edits via the existing save
  // behavior, then open the completion dialog. Finishing does NOT change visibility — that
  // is a separate, explicit decision made inside the dialog.
  const handleFinish = useCallback(async () => {
    const ok = await savePendingData();
    if (!ok) { toast.error('Please fix save errors before finishing'); return; }
    setFinishOpen(true);
  }, [savePendingData]);

  // Reuse the EXISTING visibility mechanism (PUT /api/resume { visible: true }). No second
  // visibility system. This only makes the seeker discoverable in Find Talent under existing
  // access rules; it does not expose restricted profile/resume data.
  const handleMakeVisible = useCallback(async () => {
    setFinishSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/resume`, { visible: true }, { withCredentials: true });
      setResume(res.data);
      toast.success('Your profile is now visible to employers.');
      setFinishOpen(false);
      navigate('/dashboard');
    } catch {
      toast.error('Could not update visibility. Please try again.');
    } finally {
      setFinishSaving(false);
    }
  }, [navigate]);

  const handleFinishToTransition = useCallback(() => {
    setFinishOpen(false);
    navigate('/dashboard');
  }, [navigate]);

  // ===== MOBILE SWIPE NAVIGATION =====
  // Detect touch devices
  const isTouchDevice = useCallback(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  // Handle touch start - stable callback (no state dependencies)
  const handleTouchStart = useCallback((e) => {
    // Don't trigger on input elements or contentEditable
    const tagName = e.target.tagName.toLowerCase();
    const isContentEditable = e.target.isContentEditable;
    
    if (['input', 'textarea', 'select'].includes(tagName) || isContentEditable) {
      isSwiping.current = false;
      return;
    }
    
    // Initialize touch tracking
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    // Reset end position to start position initially
    touchEndRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    isSwiping.current = true;
  }, []);

  // Handle touch move - track end position (stable callback)
  const handleTouchMove = useCallback((e) => {
    if (!isSwiping.current) return;
    
    touchEndRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  }, []);

  // Handle touch end - determine swipe direction
  // Uses refs to avoid stale closure issues with currentStep
  const handleTouchEnd = useCallback(async () => {
    if (!isSwiping.current) return;
    
    // Read current values from refs (not stale)
    const step = currentStepRef.current;
    const isNavigating = navigatingRef.current;
    const isSaving = savingRef.current;
    
    if (isNavigating || isSaving) {
      isSwiping.current = false;
      return;
    }
    
    const deltaX = touchEndRef.current.x - touchStartRef.current.x;
    const deltaY = Math.abs(touchEndRef.current.y - touchStartRef.current.y);
    
    // Reset swiping flag
    isSwiping.current = false;
    
    // Check if it's a valid horizontal swipe
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
    if (deltaY > SWIPE_VERTICAL_TOLERANCE) return; // Too much vertical movement
    
    // Swipe left = next step
    if (deltaX < -SWIPE_THRESHOLD && step < STEPS.length) {
      console.log('[Swipe] Left detected - going to step', step + 1);
      await navigateToStep(step + 1);
    }
    // Swipe right = previous step
    else if (deltaX > SWIPE_THRESHOLD && step > 1) {
      console.log('[Swipe] Right detected - going to step', step - 1);
      await navigateToStep(step - 1);
    }
  }, [navigateToStep]); // Only depends on navigateToStep, reads currentStep from ref

  // Attach touch listeners - now stable because handlers use refs
  useEffect(() => {
    if (!isTouchDevice()) return;
    
    const content = contentRef.current;
    if (!content) return;
    
    content.addEventListener('touchstart', handleTouchStart, { passive: true });
    content.addEventListener('touchmove', handleTouchMove, { passive: true });
    content.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      content.removeEventListener('touchstart', handleTouchStart);
      content.removeEventListener('touchmove', handleTouchMove);
      content.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isTouchDevice, handleTouchStart, handleTouchMove, handleTouchEnd]);

  if (loading) {
    return (
      <Layout user={user}>
        <PageContainer>
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
          </div>
        </PageContainer>
      </Layout>
    );
  }

  if (!resume) {
    return (
      <Layout user={user}>
        <PageContainer>
          <div className="text-center py-20">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Unable to Load Resume</h2>
            <p className="text-slate-600 mb-6">There was a problem loading your resume. Please try again.</p>
            <Button 
              onClick={() => window.location.reload()}
              className="bg-brand hover:bg-brand-hover text-white"
            >
              Retry
            </Button>
          </div>
        </PageContainer>
      </Layout>
    );
  }

  const CurrentStepComponent = STEPS[currentStep - 1].component;

  return (
    <Layout user={user}>
      <PageContainer>
        <div 
          ref={contentRef}
          style={{ touchAction: 'pan-y pinch-zoom' }} // Allow vertical scroll but capture horizontal swipes
          data-testid="resume-builder-content"
          className="pb-24"
        >
        {/* Step Progress Indicator Text */}
        <PageHeader 
          icon={FileText}
          title="My Resume"
          subtitle="Your living transition document."
          className="mb-4 print:hidden"
        />

        {/* Step Progress - Responsive Stepper */}
        <div className="mb-8 print:hidden" data-testid="step-progress">
          {/* Desktop: Show all steps in a row */}
          <div className="hidden md:flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1 last:flex-none">
                <button
                  data-testid={`step-${step.id}-btn`}
                  onClick={() => handleStepClick(step.id)}
                  disabled={navigating || saving}
                  className={`flex flex-col items-center gap-1 group disabled:opacity-50`}
                >
                  <div className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors ${
                    step.id === currentStep
                      ? 'border-brand bg-brand text-white shadow-sm'
                      : step.id < currentStep
                      ? 'border-[#7f9c5f] bg-[#7f9c5f] text-white'
                      : 'border-[#d6d9d2] text-[#b0b3ab] group-hover:border-[#bbbeb6]'
                  }`}>
                    {step.id < currentStep ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <span className="text-xs font-medium">{step.id}</span>
                    )}
                  </div>
                  <span className={`text-xs text-center leading-tight max-w-[70px] ${
                    step.id === currentStep ? 'font-semibold text-[#222]' : step.id < currentStep ? 'text-[#7f9c5f]' : 'text-[#b0b3ab]'
                  }`}>
                    {step.name}
                  </span>
                </button>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 min-w-[8px] ${
                    step.id < currentStep ? 'bg-[#7f9c5f]' : 'bg-[#e5e5e5]'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Mobile: adjacent step nav + step context label + slim progress bar (Back/Continue action row handles primary nav below) */}
          <div className="flex md:hidden flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <button
                data-testid="mobile-adjacent-prev"
                onClick={handlePrevious}
                disabled={currentStep === 1 || navigating || saving}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-brand disabled:opacity-0 disabled:pointer-events-none max-w-[42%]"
              >
                <ChevronLeft className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{STEPS[currentStep - 2]?.name}</span>
              </button>
              <span
                data-testid="mobile-step-context"
                className="text-xs font-semibold text-brand text-center flex-shrink-0"
              >
                Step {currentStep} of {STEPS.length}
              </span>
              <button
                data-testid="mobile-adjacent-next"
                onClick={handleNext}
                disabled={currentStep === STEPS.length || navigating || saving}
                className="flex items-center gap-1 justify-end text-xs text-slate-500 hover:text-brand disabled:opacity-0 disabled:pointer-events-none max-w-[42%]"
              >
                <span className="truncate">{STEPS[currentStep]?.name}</span>
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
              </button>
            </div>
            <p data-testid="mobile-step-name" className="text-sm font-semibold text-slate-900 text-center">
              {STEPS[currentStep - 1].name}
            </p>
            <div className="w-full h-1.5 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={STEPS.length}>
              <div
                className="h-full bg-brand rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Saving indicator */}
        {(saving || navigating) && (
          <div className="mb-4 p-2 bg-brand-light border border-brand/20 rounded-md flex items-center gap-2 text-sm text-brand print:hidden">
            <Loader2 className="w-4 h-4 animate-spin" />
            {saving ? 'Saving...' : 'Loading...'}
          </div>
        )}

        {/* Current Step Content */}
        {currentStep === 5 ? (
          // Review manages its own full-width layout
          <div data-testid={`step-${currentStep}-content`}>
            <CurrentStepComponent
              resume={resume}
              saveResume={saveResume}
              onNext={handleNext}
              onPrevious={handlePrevious}
              saving={saving}
              onResumeUpdate={setResume}
              registerPendingData={registerPendingData}
              pendingResumeRegen={pendingResumeRegen}
              onRegenComplete={() => setPendingResumeRegen(false)}
              onDismissRegen={() => setPendingResumeRegen(false)}
              onMarkStale={() => setPendingResumeRegen(true)}
              user={user}
            />
          </div>
        ) : (
          <Card className="border border-slate-200 shadow-sm p-6 md:p-8 mb-6" data-testid={`step-${currentStep}-content`}>
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight mb-6">
              {STEPS[currentStep - 1].heading || STEPS[currentStep - 1].name}
            </h2>
            <CurrentStepComponent
              resume={resume}
              saveResume={saveResume}
              onNext={handleNext}
              saving={saving}
              onResumeUpdate={setResume}
              registerPendingData={registerPendingData}
              registerStepValidator={registerStepValidator}
              goToStep={navigateToStep}
              onMilitaryChanged={currentStep === 2 ? handleMilitaryChanged : undefined}
            />
          </Card>
        )}

        {/* Navigation Buttons - unified Back/Continue (desktop + mobile). */}
        <div className={`flex justify-between print:hidden ${currentStep === 5 ? 'mt-6 pt-6 border-t border-slate-200' : ''}`}>
            {currentStep > 1 ? (
              <Button
                data-testid="previous-step-btn"
                onClick={handlePrevious}
                disabled={navigating || saving}
                variant="outline"
                className="border-slate-300 text-slate-700"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div aria-hidden="true" />
            )}

            <Button
              data-testid={currentStep === 5 ? 'finish-resume-btn' : 'next-step-btn'}
              onClick={currentStep === 5 ? handleFinish : handleNext}
              disabled={navigating || saving}
              className="bg-brand hover:bg-brand-hover text-white"
            >
              {navigating || saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : currentStep === 5 ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Finish Resume
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </PageContainer>

      {/* Resume-completion dialog (final step). Finishing never auto-changes visibility. */}
      <Dialog open={finishOpen} onOpenChange={(o) => !finishSaving && setFinishOpen(o)}>
        <DialogContent className="max-w-md" data-testid="finish-resume-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900" data-testid="finish-resume-title">
              Your resume is ready.
            </DialogTitle>
            <DialogDescription className="text-slate-500 pt-1" data-testid="finish-resume-desc">
              {resume?.visible
                ? 'Your profile is already visible to employers.'
                : 'Would you like employers in Sergeant TAP to be able to find your profile?'}
            </DialogDescription>
          </DialogHeader>

          {!resume?.visible && (
            <div className="flex items-start gap-2.5 p-3 mt-1 rounded-lg bg-brand-light/60 border border-brand/15 text-xs text-slate-600" data-testid="finish-visibility-note">
              <Eye className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
              <span>
                Making your profile visible lets employers discover you in Find Talent. It does not
                expose your home address or other restricted details, and existing profile access
                rules still apply. You can stay private by choosing Not Now.
              </span>
            </div>
          )}

          <DialogFooter className="mt-4">
            {resume?.visible ? (
              <Button
                onClick={handleFinishToTransition}
                className="bg-brand hover:bg-brand-hover text-white w-full sm:w-auto"
                data-testid="finish-go-transition-btn"
              >
                Go to Transition
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleFinishToTransition}
                  disabled={finishSaving}
                  className="border-slate-300 text-slate-700"
                  data-testid="finish-not-now-btn"
                >
                  Not Now
                </Button>
                <Button
                  onClick={handleMakeVisible}
                  disabled={finishSaving}
                  className="bg-brand hover:bg-brand-hover text-white"
                  data-testid="finish-make-visible-btn"
                >
                  {finishSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Make My Profile Visible
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default ResumeBuilder;
